import React, { useState } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import { invalidateCategoriesTree } from "../../../../hooks/useCategoriesTree";
import { createCategory } from "../../../../services/categoriesApi";
import { ICON_NAMES, renderCategoryIcon } from "../../../../datas/categoryIcons";

const iconOptionContent = (name) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
    <span style={{ display: "inline-block", width: 20, textAlign: "center" }}>
      {renderCategoryIcon(name, "#333", "lg")}
    </span>
    {name}
  </span>
);

const AVAILABILITY_OPTIONS = [
  { key: "always", text: "Toujours", value: "always" },
  { key: "lunch", text: "Midi (avant 18h)", value: "lunch" },
  { key: "dinner", text: "Soir (après 18h)", value: "dinner" },
];

// Génère un slug stable depuis le nom : NFD pour décomposer les accents,
// remplace tout ce qui n'est pas alphanum par des tirets, trim les tirets.
function slugify(name) {
  return (name || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const AddCategory = ({ open, onClose, parentId, setAppMessage }) => {
  const [form, setForm] = useState({
    name: "",
    icon: "",
    iconColor: "#ffffff",
    availability: "always",
    badgeIcon: "",
    badgeColor: "#ffd700",
  });
  const [saving, setSaving] = useState(false);
  const isTopLevel = !parentId;
  const computedSlug = slugify(form.name);

  const handleSave = async () => {
    if (!form.name || !computedSlug) {
      setAppMessage({ success: false, message: "Nom requis (avec au moins un caractère alphanumérique)" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        slug: computedSlug,
        parentId: parentId || null,
        icon: form.icon || null,
        iconColor: form.iconColor,
        badge: form.badgeIcon
          ? { icon: form.badgeIcon, color: form.badgeColor }
          : null,
      };
      if (isTopLevel) payload.availability = form.availability;
      await createCategory(payload);
      invalidateCategoriesTree();
      setForm({
        name: "",
        icon: "",
        iconColor: "#ffffff",
        availability: "always",
        badgeIcon: "",
        badgeColor: "#ffd700",
      });
      onClose();
    } catch (e) {
      setAppMessage({
        success: false,
        message: e.response?.data?.message || "Erreur création",
      });
    } finally {
      setSaving(false);
    }
  };

  const iconOptions = [
    { key: "_none", text: "(aucune)", value: "" },
    ...ICON_NAMES.map((n) => ({
      key: n,
      text: n,
      value: n,
      content: iconOptionContent(n),
    })),
  ];

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header>
        Nouvelle catégorie {parentId ? "(sous-catégorie)" : "(racine)"}
      </Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Input
            label="Nom"
            value={form.name}
            onChange={(_, { value }) => setForm({ ...form, name: value })}
          />
          <Form.Field>
            <label>Slug (auto)</label>
            <code
              style={{
                display: "inline-block",
                padding: "8px 12px",
                background: "#f5f5f5",
                color: "#333",
                borderRadius: 4,
              }}
            >
              {computedSlug || <span style={{ opacity: 0.5 }}>—</span>}
            </code>
          </Form.Field>
          <Form.Select
            label="Icône"
            options={iconOptions}
            value={form.icon}
            onChange={(_, { value }) => setForm({ ...form, icon: value })}
          />
          <Form.Input
            label="Couleur icône"
            type="color"
            value={form.iconColor}
            onChange={(_, { value }) => setForm({ ...form, iconColor: value })}
          />
          <Form.Select
            label="Badge superposé (optionnel)"
            options={iconOptions}
            value={form.badgeIcon}
            onChange={(_, { value }) => setForm({ ...form, badgeIcon: value })}
          />
          {form.badgeIcon && (
            <Form.Input
              label="Couleur du badge"
              type="color"
              value={form.badgeColor}
              onChange={(_, { value }) => setForm({ ...form, badgeColor: value })}
            />
          )}
          {isTopLevel && (
            <Form.Select
              label="Apparition"
              options={AVAILABILITY_OPTIONS}
              value={form.availability}
              onChange={(_, { value }) => setForm({ ...form, availability: value })}
            />
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Annuler</Button>
        <Button primary loading={saving} onClick={handleSave}>
          Créer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default AddCategory;
